import BaseService from './BaseService.js';
import FileService from './FileService.js';
import { db } from '../../config/firebase';
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  increment
} from 'firebase/firestore';
import { httpsCallable, getFunctions } from 'firebase/functions';

/**
 * Ads Service for handling all advertisement-related Firebase operations
 * Extends BaseService to provide ad-specific functionality with caching
 */
class AdsService extends BaseService {
  constructor() {
    super('ads');
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes for ads
  }

  /**
   * Get all ads (admin only)
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Array>} Array of all ads
   */
  async getAllAds(useCache = true) {
    try {
      const ads = await this.getDocuments({}, { field: 'created', direction: 'desc' }, null, useCache);
      return ads.map(ad => this.addComputedProperties(ad));
    } catch (error) {
      console.error('Error fetching all ads:', error);
      throw error;
    }
  }

  /**
   * Get user's ads
   * @param {string} uid - User ID
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Array>} Array of user's ads
   */
  async getUserAds(uid, useCache = true) {
    if (!uid) {
      throw new Error('User ID is required');
    }

    try {
      const ads = await this.getDocuments(
        { uid: uid }, 
        { field: 'created', direction: 'desc' }, 
        null, 
        useCache
      );
      return ads.map(ad => this.addComputedProperties(ad));
    } catch (error) {
      console.error('Error fetching user ads:', error);
      throw error;
    }
  }

  /**
   * Get current authenticated user
   * @returns {Promise<Object|null>} Current user object or null
   */
  async getCurrentUser() {
    try {
      // Import Firebase auth dynamically to avoid circular dependencies
      const { getAuth, onAuthStateChanged } = await import('firebase/auth');
      const auth = getAuth();
      
      return new Promise((resolve) => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
          unsubscribe();
          resolve(user);
        });
      });
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  }

  /**
   * Get a single ad by key
   * @param {string} adKey - Ad key
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object|null>} Ad object or null
   */
  async getAd(adKey, useCache = true) {
    if (!adKey) {
      throw new Error('Ad key is required');
    }

    try {
      const ad = await this.getDocument(adKey, useCache);
      return ad ? this.addComputedProperties(ad) : null;
    } catch (error) {
      console.error('Error fetching ad:', error);
      
      // Try to handle auth error and retry once
      const tokenRefreshed = await this.handleAuthError(error, 'getAd');
      if (tokenRefreshed) {
        try {
          const ad = await this.getDocument(adKey, useCache);
          return ad ? this.addComputedProperties(ad) : null;
        } catch (retryError) {
          console.error('Error fetching ad after token refresh:', retryError);
          throw retryError;
        }
      }
      
      throw error;
    }
  }

  /**
   * Create a new ad
   * @param {Object} adData - Ad data
   * @param {string} imageObject - Landscape image data URL
   * @param {string} imagePortObject - Portrait image data URL
   * @returns {Promise<Object>} Created ad object
   */
  async createAd(adData, imageObject, imagePortObject) {
    try {
      // Get current user from auth context (following UserService pattern)
      const currentUser = await this.getCurrentUser();
      
      if (!currentUser) {
        throw new Error('User must be authenticated to create ads');
      }

      // Prepare ad data with all required fields
      const adKey = null; // Will be set after document creation
      adData.uid = currentUser.uid;
      adData.created = new Date().getTime();
      adData.approved = 1; // Auto-approved for admin-created ads
      adData.active = adData.active !== undefined ? adData.active : true; // Use provided value or default to true
      adData.hits = 0; // Initialize hit counter
      adData.lastHit = null; // No hits yet
      adData.isSystemAd = true; // System ad
      adData.modified = null; // No modifications yet

      // Create the ad document first
      const docRef = await addDoc(collection(db, this.collectionName), adData);
      adData.key = docRef.id;

      // Upload images
      let fileIndex = 0;
      const promises = [];
      const rawImages = [imageObject, imagePortObject];
      
      rawImages.forEach((image, index) => {
        let fileName = docRef.id + "_ad_image" + (index == 1 ? "_port" : "");
        
        if (image.indexOf("data:image/png;base64") !== -1) {
          fileName += ".png";
        } else if (image.indexOf("data:image/jpg;base64") !== -1) {
          fileName += ".jpg";
        } else {
          fileName += ".jpeg";
        }

        promises.push(
          FileService.uploadFile(fileName, image, () => {
            fileIndex++;
            // TODO: Add progress callback handling
          })
        );
      });

      const urls = await Promise.all(promises);
      
      // Update ad with image URLs
      const updateData = {
        imageUrl: urls[0],
        imagePortUrl: urls[1],
        approved: 1
      };

      await updateDoc(doc(db, this.collectionName, docRef.id), updateData);

      // Clear cache for this user's ads
      this.clearCache(`user_ads_${currentUser.uid}`);
      this.clearCache(`all_ads`);

      // Return ad with computed properties
      const finalAdData = { ...adData, ...updateData };
      return this.addComputedProperties(finalAdData);
    } catch (error) {
      console.error('Error creating ad:', error);
      throw error;
    }
  }

  /**
   * Modify an existing ad
   * @param {string} adKey - Ad key
   * @param {Object} adData - Updated ad data
   * @param {string} imageObject - Landscape image data URL or URL
   * @param {string} imagePortObject - Portrait image data URL or URL
   * @returns {Promise<Object>} Updated ad object
   */
  async modifyAd(adKey, adData, imageObject, imagePortObject) {
    try {
      // Get current user from auth context (following UserService pattern)
      const currentUser = await this.getCurrentUser();
      
      if (!currentUser) {
        throw new Error('User must be authenticated to modify ads');
      }

      adData.modified = new Date().getTime();

      // Check if images need to be uploaded
      if (imageObject.indexOf("http") === 0 && imagePortObject.indexOf("http") === 0) {
        // Images are already URLs, just update the document
        await updateDoc(doc(db, this.collectionName, adKey), adData);
      } else {
        // Need to upload new images
        let fileIndex = 0;
        const promises = [];
        const rawImages = [];

        if (imageObject.indexOf("http") !== 0) {
          rawImages.push({ type: "imageUrl", data: imageObject });
        }
        if (imagePortObject.indexOf("http") !== 0) {
          rawImages.push({ type: "imagePortUrl", data: imagePortObject });
        }

        rawImages.forEach((image) => {
          let fileName = adKey + "_ad_image" + (image.type == "imagePortUrl" ? "_port" : "");
          
          if (imageObject.indexOf("data:image/png;base64") !== -1) {
            fileName += ".png";
          } else if (imageObject.indexOf("data:image/jpg;base64") !== -1) {
            fileName += ".jpg";
          } else {
            fileName += ".jpeg";
          }

          promises.push(
            FileService.uploadFile(fileName, image.data, () => {
              fileIndex++;
              // TODO: Add progress callback handling
            })
          );
        });

        const urls = await Promise.all(promises);
        
        // Update ad data with new URLs
        urls.forEach((url, index) => {
          adData[rawImages[index].type] = url;
        });

        adData.approved = 1; // Auto-approved for admin-modified ads

        await updateDoc(doc(db, this.collectionName, adKey), adData);
      }

      // Clear cache
      this.clearCache(`ad_${adKey}`);
      this.clearCache(`user_ads_${currentUser.uid}`);
      this.clearCache(`all_ads`);

      // Return ad with computed properties and preserve existing image URLs
      const finalAdData = { 
        ...adData, 
        key: adKey,
        imageUrl: adData.imageUrl || imageObject,
        imagePortUrl: adData.imagePortUrl || imagePortObject
      };
      return this.addComputedProperties(finalAdData);
    } catch (error) {
      console.error('Error modifying ad:', error);
      throw error;
    }
  }

  /**
   * Delete an ad
   * @param {string} adKey - Ad key
   * @returns {Promise<void>}
   */
  async deleteAd(adKey) {
    try {
      // Delete the document
      await deleteDoc(doc(db, this.collectionName, adKey));
      
      // Delete associated files
      await FileService.deleteFiles(adKey);

      // Clear cache
      this.clearCache(`ad_${adKey}`);
      this.clearCache(`all_ads`);
      
      // Clear user ads cache (we don't know which user, so clear all)
      this.clearCache();
    } catch (error) {
      console.error('Error deleting ad:', error);
      throw error;
    }
  }
  /**
   * Toggle ad active status
   * @param {string} adKey - Ad key
   * @param {boolean} active - Active status
   * @returns {Promise<void>}
   */
  async toggleAdActive(adKey, active) {
    try {
      await updateDoc(doc(db, this.collectionName, adKey), { active: active });
      
      // Clear cache
      this.clearCache(`ad_${adKey}`);
      this.clearCache(`all_ads`);
    } catch (error) {
      console.error('Error toggling ad active status:', error);
      throw error;
    }
  }

  /**
   * Get active ads only
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Array>} Array of active ads
   */
  async getActiveAds(useCache = true) {
    try {
      const ads = await this.getDocuments(
        { active: true, approved: 1 }, 
        { field: 'created', direction: 'desc' }, 
        null, 
        useCache
      );
      return ads.map(ad => this.addComputedProperties(ad));
    } catch (error) {
      console.error('Error fetching active ads:', error);
      throw error;
    }
  }



  /**
   * Record ad hit/click
   * @param {string} adKey - Ad key
   * @returns {Promise<void>}
   */
  async hitAd(adKey) {
    try {
      // Update local hit counter and last hit time
      const currentTime = new Date().getTime();
      await updateDoc(doc(db, this.collectionName, adKey), {
        hits: increment(1),
        lastHit: currentTime
      });

      // Also call the cloud function for additional processing
      const functions = getFunctions();
      const hitClientAd = httpsCallable(functions, 'hitClientAd');
      await hitClientAd({ adKey: adKey });
    } catch (error) {
      console.error('Error recording ad hit:', error);
      throw error;
    }
  }

  /**
   * Add computed properties to ad object
   * @param {Object} ad - Ad object
   * @returns {Object} Ad object with computed properties
   */
  addComputedProperties(ad) {
    return {
      ...ad,
      isApproved: ad.approved === 1,
      isRejected: ad.approved === 2,
      isPending: ad.approved === 0,
      statusText: ad.approved === 1 ? 'Approved' : ad.approved === 2 ? 'Rejected' : 'Pending',
      createdDate: ad.created ? new Date(ad.created).toLocaleDateString() : 'Unknown',
      modifiedDate: ad.modified ? new Date(ad.modified).toLocaleDateString() : null,
      lastHitDate: ad.lastHit ? new Date(ad.lastHit).toLocaleDateString() : 'Never',
      isActive: ad.active === true
    };
  }

  /**
   * Subscribe to ads changes
   * @param {Function} callback - Callback function
   * @param {Object} filters - Filters to apply
   * @returns {Function} Unsubscribe function
   */
  subscribeToAds(callback, filters = {}) {
    return this.subscribeToDocuments(filters, callback);
  }

  /**
   * Subscribe to user ads changes
   * @param {string} uid - User ID
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  subscribeToUserAds(uid, callback) {
    return this.subscribeToDocuments({ uid: uid }, callback);
  }

  /**
   * Get ads statistics
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object>} Ads statistics
   */
  async getAdsStats(useCache = true) {
    try {
      const allAds = await this.getAllAds(useCache);
      
      const stats = {
        total: allAds.length,
        active: allAds.filter(ad => ad.isActive).length,
        inactive: allAds.filter(ad => !ad.isActive).length,
        totalHits: allAds.reduce((sum, ad) => sum + (ad.hits || 0), 0),
        averageHits: allAds.length > 0 ? Math.round(allAds.reduce((sum, ad) => sum + (ad.hits || 0), 0) / allAds.length) : 0,
        recentAds: allAds.filter(ad => {
          const oneWeekAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
          return ad.created && ad.created > oneWeekAgo;
        }).length
      };

      return stats;
    } catch (error) {
      console.error('Error fetching ads stats:', error);
      throw error;
    }
  }

  /**
   * Get ads by date range
   * @param {number} startDate - Start timestamp
   * @param {number} endDate - End timestamp
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Array>} Array of ads in date range
   */
  async getAdsByDateRange(startDate, endDate, useCache = true) {
    try {
      const allAds = await this.getAllAds(useCache);
      return allAds.filter(ad => 
        ad.created && ad.created >= startDate && ad.created <= endDate
      );
    } catch (error) {
      console.error('Error fetching ads by date range:', error);
      throw error;
    }
  }

  /**
   * Get required indexes for ads collection
   * @returns {Array} Array of required indexes
   */
  getRequiredIndexes() {
    return [
      {
        collection: 'ads',
        fields: [
          { fieldPath: 'uid', order: 'ASCENDING' },
          { fieldPath: 'created', order: 'DESCENDING' }
        ]
      },
      {
        collection: 'ads',
        fields: [
          { fieldPath: 'approved', order: 'ASCENDING' },
          { fieldPath: 'created', order: 'DESCENDING' }
        ]
      }
    ];
  }

  /**
   * Log index instructions for development
   */
  logIndexInstructions() {
    console.log('📋 Required Firestore Indexes for AdsService:');
    console.log('Collection: ads');
    console.log('Index 1: uid (ASCENDING) + created (DESCENDING)');
    console.log('Index 2: approved (ASCENDING) + created (DESCENDING)');
    console.log('Index 3: active (ASCENDING) + created (DESCENDING)');
    console.log('');
    console.log('💡 To create these indexes:');
    console.log('1. Go to Firebase Console > Firestore Database > Indexes');
    console.log('2. Click "Add Index"');
    console.log('3. Collection ID: ads');
    console.log('4. Add the fields in the order specified above');
    console.log('5. Click "Create Index"');
  }
}

// Create and export a singleton instance
const adsService = new AdsService();
export default adsService; 